/**
 * Compass — renders a compass rose with an optional direction indicator.
 *
 * data: { direction: "NE" }           → compass with needle pointing NE
 * data: { directions: ["N","SE","?"] } → sequence of mini compasses
 *
 * Directions: N, NE, E, SE, S, SO, O, NO (French) or NW, SW, W (English)
 */

const DIR_ANGLES = {
    'N': 0, 'NE': 45, 'E': 90, 'SE': 135,
    'S': 180, 'SO': 225, 'SW': 225, 'O': 270, 'W': 270, 'NO': 315, 'NW': 315,
};

const DIR_LABELS = ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO'];

function CompassNeedle({ cx, cy, angle, len }) {
    const a = (angle - 90) * Math.PI / 180;
    const tipX = cx + Math.cos(a) * len;
    const tipY = cy + Math.sin(a) * len;
    const tailLen = len * 0.35;
    const tailX = cx - Math.cos(a) * tailLen;
    const tailY = cy - Math.sin(a) * tailLen;
    return (
        <g>
            {/* Red needle — points to direction */}
            <line x1={cx} y1={cy} x2={tipX} y2={tipY}
                stroke="#dc2626" strokeWidth="3" strokeLinecap="round" />
            {/* Grey tail */}
            <line x1={cx} y1={cy} x2={tailX} y2={tailY}
                stroke="#94a3b8" strokeWidth="3" strokeLinecap="round" />
            {/* Hub */}
            <circle cx={cx} cy={cy} r={4} fill="#1e293b" stroke="#fff" strokeWidth="1.5" />
        </g>
    );
}

function CompassDial({ direction, size = 120, isMystery = false }) {
    const angle = DIR_ANGLES[direction?.toUpperCase()] ?? null;
    const r = size / 2;
    const tickR = r - 4;
    const labelR = r - 16;
    const needleLen = r - 28;

    return (
        <div className={`qv-compass ${isMystery ? 'qv-compass--mystery' : ''}`}
            style={{ width: size, height: size }}>
            <svg viewBox={`0 0 ${size} ${size}`} className="qv-compass__svg">
                {/* Outer circle */}
                <circle cx={r} cy={r} r={r - 2} className="qv-compass__ring" />

                {/* Tick marks + labels */}
                {DIR_LABELS.map((label, i) => {
                    const a = (i * 45 - 90) * Math.PI / 180;
                    const isCardinal = i % 2 === 0;
                    const tx = r + Math.cos(a) * tickR;
                    const ty = r + Math.sin(a) * tickR;
                    const lx = r + Math.cos(a) * labelR;
                    const ly = r + Math.sin(a) * labelR;
                    return (
                        <g key={label}>
                            <circle cx={tx} cy={ty} r={isCardinal ? 2 : 1}
                                className="qv-compass__tick" />
                            <text x={lx} y={ly}
                                textAnchor="middle" dominantBaseline="central"
                                className={`qv-compass__label ${isCardinal ? 'qv-compass__label--cardinal' : ''}`}>
                                {label}
                            </text>
                        </g>
                    );
                })}

                {/* Needle */}
                {angle !== null && !isMystery && <CompassNeedle cx={r} cy={r} angle={angle} len={needleLen} />}

                {/* Mystery */}
                {isMystery && (
                    <text x={r} y={r} textAnchor="middle" dominantBaseline="central"
                        className="qv-compass__mystery">?</text>
                )}
            </svg>
        </div>
    );
}

export default function Compass({ data }) {
    // Sequence mode
    if (data?.directions && Array.isArray(data.directions)) {
        return (
            <div className="qv-compasses">
                {data.directions.map((dir, i) => (
                    <CompassDial key={i} direction={dir} size={100}
                        isMystery={dir === '?'} />
                ))}
            </div>
        );
    }
    // Single compass
    if (data?.direction) {
        return (
            <div className="qv-compasses">
                <CompassDial direction={data.direction} size={140}
                    isMystery={data.direction === '?'} />
            </div>
        );
    }
    return null;
}
